"use client";

import _slicedToArray from "/home/circleci/project/web-front/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/slicedToArray";
import { TabScopedContextProvider, useTabProviderContext } from "../__chunks/BSRZWVZO.js";
import { useCollectionItem } from "../__chunks/SZHY72KI.js";
import "../__chunks/APGXEJ2Q.js";
import "../__chunks/G4PVPWEW.js";
import { useDisclosureContent } from "../__chunks/UYRBEP7M.js";
import "../__chunks/CMXOY7UW.js";
import "../__chunks/JDEWEW7S.js";
import { useFocusable } from "../__chunks/Z4WWVOIF.js";
import "../__chunks/SWN3JYXT.js";
import { createElement, createHook, forwardRef } from "../__chunks/7FZLUSKW.js";
import { useDisclosureStore } from "../__chunks/C76P7QQC.js";
import { useStoreState } from "../__chunks/CTKPPFRS.js";
import { useEvent, useId, useMergeRefs, useWrapElement } from "../__chunks/HNHAUIIY.js";
import "../__chunks/SK3NAZA3.js";
import { __objRest, __spreadProps, __spreadValues } from "../__chunks/3YLGPPWQ.js";

// src/tab/tab-panel.tsx
import { getAllTabbableIn } from "@ariakit/core/utils/focus";
import { invariant } from "@ariakit/core/utils/misc";
import { useCallback, useEffect, useRef, useState } from "react";
import { jsx } from "react/jsx-runtime";
var TagName = "div";
var useTabPanel = createHook(function useTabPanel2(_a) {
  var _b = _a,
    store = _b.store,
    unmountOnHide = _b.unmountOnHide,
    tabIdProp = _b.tabId,
    getItemProp = _b.getItem,
    scrollRestoration = _b.scrollRestoration,
    scrollElement = _b.scrollElement,
    props = __objRest(_b, ["store", "unmountOnHide", "tabId", "getItem", "scrollRestoration", "scrollElement"]);
  var context = useTabProviderContext();
  store = store || context;
  invariant(store, process.env.NODE_ENV !== "production" && "TabPanel must receive a `store` prop or be wrapped in a TabProvider component.");
  var ref = useRef(null);
  var id = useId(props.id);
  var tabId = useStoreState(store.panels, function () {
    var _a2;
    return tabIdProp || ((_a2 = store == null ? void 0 : store.panels.item(id)) == null ? void 0 : _a2.tabId);
  });
  var open = useStoreState(store, function (state) {
    return !!tabId && state.selectedId === tabId;
  });
  var disclosure = useDisclosureStore({
    open: open
  });
  var mounted = useStoreState(disclosure, "mounted");
  var scrollPositionRef = useRef(/* @__PURE__ */new Map());
  var getScrollElement = useEvent(function () {
    var panelElement = ref.current;
    if (!panelElement) return null;
    if (!scrollElement) return panelElement;
    if (typeof scrollElement === "function") {
      return scrollElement(panelElement);
    }
    if ("current" in scrollElement) {
      return scrollElement.current;
    }
    return scrollElement;
  });
  useEffect(function () {
    var _a2, _b2;
    if (!scrollRestoration) return;
    if (!mounted) return;
    var element = getScrollElement();
    if (!element) return;
    if (scrollRestoration === "reset") {
      element.scroll(0, 0);
      return;
    }
    if (!tabId) return;
    var position = scrollPositionRef.current.get(tabId);
    element.scroll((_a2 = position == null ? void 0 : position.x) != null ? _a2 : 0, (_b2 = position == null ? void 0 : position.y) != null ? _b2 : 0);
    var onScroll = function onScroll() {
      scrollPositionRef.current.set(tabId, {
        x: element.scrollLeft,
        y: element.scrollTop
      });
    };
    element.addEventListener("scroll", onScroll);
    return function () {
      element.removeEventListener("scroll", onScroll);
    };
  }, [scrollRestoration, mounted, tabId, getScrollElement, store]);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    hasTabbableChildren = _useState2[0],
    setHasTabbableChildren = _useState2[1];
  useEffect(function () {
    var element = ref.current;
    if (!element) return;
    var tabbable = getAllTabbableIn(element);
    setHasTabbableChildren(!!tabbable.length);
  }, []);
  var getItem = useCallback(function (item) {
    var nextItem = __spreadProps(__spreadValues({}, item), {
      id: id || item.id,
      tabId: tabIdProp
    });
    if (getItemProp) {
      return getItemProp(nextItem);
    }
    return nextItem;
  }, [id, tabIdProp, getItemProp]);
  var onKeyDownProp = props.onKeyDown;
  var onKeyDown = useEvent(function (event) {
    onKeyDownProp == null ? void 0 : onKeyDownProp(event);
    if (event.defaultPrevented) return;
    if (!(store == null ? void 0 : store.composite)) return;
    var keyMap = {
      ArrowLeft: store.previous,
      ArrowRight: store.next,
      Home: store.first,
      End: store.last
    };
    var action = keyMap[event.key];
    if (!action) return;
    var _store$getState = store.getState(),
      selectedId = _store$getState.selectedId;
    var nextId = action({
      activeId: selectedId
    });
    if (!nextId) return;
    event.preventDefault();
    store.move(nextId);
  });
  props = useWrapElement(props, function (element) {
    return /* @__PURE__ */jsx(TabScopedContextProvider, {
      value: store,
      children: element
    });
  }, [store]);
  props = __spreadProps(__spreadValues({
    id: id,
    role: "tabpanel",
    "aria-labelledby": tabId || void 0
  }, props), {
    children: unmountOnHide && !mounted ? null : props.children,
    ref: useMergeRefs(ref, props.ref),
    onKeyDown: onKeyDown
  });
  props = useFocusable(__spreadValues({
    // If the tab panel is rendered as part of another composite widget such
    // as combobox, it should not be focusable.
    focusable: !store.composite && !hasTabbableChildren
  }, props));
  props = useDisclosureContent(__spreadValues({
    store: disclosure
  }, props));
  props = useCollectionItem(__spreadProps(__spreadValues({
    store: store.panels
  }, props), {
    getItem: getItem
  }));
  return props;
});
var TabPanel = forwardRef(function TabPanel2(props) {
  var htmlProps = useTabPanel(props);
  return createElement(TagName, htmlProps);
});
export { TabPanel, useTabPanel };